import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  message,
  Popconfirm,
  Tag,
  Select,
  Card,
  Typography,
  Row,
  Col,
  DatePicker,
  Alert,
  Divider,
  Spin,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  LinkOutlined,
  DisconnectOutlined,
  SearchOutlined,
  ReloadOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import styled from "@emotion/styled";
import dayjs from "dayjs";
import {
  fetchMasterContainers,
  createMasterContainer,
  updateMasterContainer,
  deleteMasterContainer,
  addContainerToMaster,
  removeContainerFromMaster,
  getAvailableContainers,
  bulkUpdateContainersInMaster,
  bulkUpdateBookingsInMaster,
  getUpdatePreview,
} from "../../services/masterContainerService";
import { fetchAgents } from "../../services/agentService";
import type {
  Agent,
  MasterContainerWithContainers,
} from "../../types/booking";
import { formatDate } from "../../utils/bookingUtils";
import { useSearchParams } from "react-router-dom";
import {
  normalizeBookingStatus,
  statusColors,
} from "../../utils/bookingStatus";
import { getMasterTransportTypeName } from "../../utils/masterContainerTypeUtils";
import { MasterContainerAwbBlSection } from "./MasterContainerAwbBlSection";
import { MasterContainerPortFields } from "./MasterContainerPortFields";
import { supabase } from "../../lib/supabase";
import { MasterContainersNestedExpand } from "./MasterContainersNestedExpand";
import { useOriginPorts } from "../../hooks/useOriginPorts";
import { useDestinationPorts } from "../../hooks/useDestinationPorts";

const { Text, Title } = Typography;
const { Option } = Select;

// Styled components for compact table
const CompactTable = styled.div`
  .compact-table {
    .ant-table {
      font-size: 13px;
    }

    .ant-table-thead > tr > th {
      padding: 8px 12px;
    }

    .ant-table-tbody > tr > td {
      padding: 6px 12px;
    }

    .ant-table-cell {
      white-space: nowrap;
    }

    .ant-tag {
      margin-right: 0;
      font-size: 11px;
      padding: 0 4px;
      line-height: 18px;
    }

    .ant-btn {
      font-size: 12px;
      padding: 0 6px;
      height: 22px;
    }

    .ant-space {
      gap: 4px !important;
    }
  }
`;

interface MasterContainersListProps {
  onRefresh?: () => void;
}

const MasterContainersList: React.FC<MasterContainersListProps> = ({
  onRefresh,
}) => {
  const { originPorts } = useOriginPorts();
  const { destinationPorts } = useDestinationPorts();
  const [masterContainers, setMasterContainers] = useState<
    MasterContainerWithContainers[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isContainerModalVisible, setIsContainerModalVisible] = useState(false);
  const [selectedMaster, setSelectedMaster] =
    useState<MasterContainerWithContainers | null>(null);
  const [form] = Form.useForm();
  const [containerForm] = Form.useForm();
  const [availableContainers, setAvailableContainers] = useState<
    Array<{
      id: number;
      reference: string | null;
      status: string | null;
      etd: string | null;
      eta: string | null;
      track_and_trace: string | null;
      transport_type_info?: {
        name: string;
        abbreviation: string;
      };
    }>
  >([]);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [transportTypes, setTransportTypes] = useState<
    { id: number; name: string }[]
  >([]);

  // Bulk update states
  const [isBulkUpdateModalVisible, setIsBulkUpdateModalVisible] =
    useState(false);
  const [bulkUpdateForm] = Form.useForm();
  const [selectedMasterForBulk, setSelectedMasterForBulk] =
    useState<MasterContainerWithContainers | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [updatePreview, setUpdatePreview] = useState<{
    containers: Array<{
      id: number;
      reference: string | null;
      status: string | null;
      eta: string | null;
      eta_customer: string | null;
      etd: string | null;
      delayed_etd: string | null;
      track_and_trace: string | null;
    }>;
    bookings: Array<{
      id: number;
      reference: string | null;
      status: string | null;
      container_id: number | null;
    }>;
  }>({ containers: [], bookings: [] });
  const [previewLoading, setPreviewLoading] = useState(false);

  // URL deep-link: highlight + optionally open manage modal
  const [searchParams, setSearchParams] = useSearchParams();
  const [highlightedMasterId, setHighlightedMasterId] = useState<number | null>(
    null
  );
  const deepLinkOpenedRef = useRef<number | null>(null);

  const loadMasterContainers = async () => {
    setLoading(true);
    try {
      const result = await fetchMasterContainers();
      if (result.error) {
        message.error("Failed to load bills of lading (B/L)");
      } else {
        setMasterContainers(result.data);
      }
    } catch (error) {
      console.error("Error loading master containers:", error);
      message.error("Failed to load bills of lading (B/L)");
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableContainers = async (transportTypeId?: number | null) => {
    try {
      const result = await getAvailableContainers({ transportTypeId });
      if (result.error) {
        console.error("Error loading available containers:", result.error);
        message.error("Failed to load available containers");
      } else {
        // Transform the data to match expected types
        const transformedData = result.data.map(
          (container: {
            id: number;
            reference: string | null;
            status: string | null;
            etd: string | null;
            eta: string | null;
            track_and_trace: string | null;
            transport_type_info?:
              | {
                  name: string;
                  abbreviation: string;
                }
              | Array<{
                  name: string;
                  abbreviation: string;
                }>;
          }) => ({
            ...container,
            transport_type_info: Array.isArray(container.transport_type_info)
              ? container.transport_type_info[0]
              : container.transport_type_info,
          })
        );
        setAvailableContainers(transformedData);
      }
    } catch (error) {
      console.error("Error loading available containers:", error);
      message.error("Failed to load available containers");
    }
  };

  useEffect(() => {
    loadMasterContainers();
    fetchAgents().then((result) => {
      if (!result.error) setAgents(result.data);
    });
    supabase
      .from("booking_transport_types")
      .select("id, name")
      .order("name")
      .then(({ data }) => setTransportTypes(data || []));
  }, []);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // Prepare the data object
      const masterContainerData = {
        reference: values.reference,
        transport_type: values.transport_type ?? null,
        agent_id: values.agent_id ?? null,
        origin_port: values.origin_port ?? null,
        destination_port: values.destination_port ?? null,
        eta_customer: values.eta_customer
          ? dayjs(values.eta_customer).format("YYYY-MM-DD")
          : null,
        eta: values.eta ? dayjs(values.eta).format("YYYY-MM-DD") : null,
        etd: values.etd ? dayjs(values.etd).format("YYYY-MM-DD") : null,
        delayed_etd: values.delayed_etd
          ? dayjs(values.delayed_etd).format("YYYY-MM-DD")
          : null,
        status: values.status || null,
        track_and_trace: values.track_and_trace || null,
      };

      const result = await createMasterContainer(masterContainerData);

      if (result.error) {
        message.error("Failed to create bill of lading (B/L)");
        return;
      }

      message.success("Bill of lading (B/L) created successfully");
      setIsModalVisible(false);
      loadMasterContainers();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error("Error saving master container:", error);
      message.error("Failed to save bill of lading (B/L)");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      setLoading(true);
      const result = await deleteMasterContainer(id);

      if (result.error) {
        message.error("Failed to delete bill of lading (B/L)");
        return;
      }

      message.success("Bill of lading (B/L) deleted successfully");
      loadMasterContainers();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error("Error deleting master container:", error);
      message.error("Failed to delete bill of lading (B/L)");
    } finally {
      setLoading(false);
    }
  };

  const handleAddContainer = async () => {
    try {
      const values = await containerForm.validateFields();
      if (!selectedMaster) return;

      const result = await addContainerToMaster(
        selectedMaster.id,
        values.container_id
      );

      if (result.error) {
        message.error(
          (result.error as Error).message || "Failed to add container"
        );
        return;
      }

      message.success("Container added to bill of lading (B/L) successfully");
      setIsContainerModalVisible(false);
      loadMasterContainers();
      loadAvailableContainers();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error("Error adding container:", error);
      message.error("Failed to add container");
    }
  };

  const handleRemoveContainer = async (
    masterId: number,
    containerId: number
  ) => {
    try {
      const result = await removeContainerFromMaster(masterId, containerId);

      if (result.error) {
        message.error("Failed to remove container");
        return;
      }

      message.success("Container removed from bill of lading (B/L) successfully");
      loadMasterContainers();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error("Error removing container:", error);
      message.error("Failed to remove container");
    }
  };

  const showModal = () => {
    form.resetFields();
    setIsModalVisible(true);
  };

  const showContainerModal = async (master: MasterContainerWithContainers) => {
    setSelectedMaster(master);
    await loadAvailableContainers(master.transport_type);
    containerForm.resetFields();
    containerForm.setFieldsValue({
      reference: master.reference || "",
      transport_type: master.transport_type || undefined,
      agent_id: master.agent_id || undefined,
      origin_port: master.origin_port || undefined,
      destination_port: master.destination_port || undefined,
    });
    setIsContainerModalVisible(true);
  };

  useEffect(() => {
    const masterId = searchParams.get("master_id");
    const action = searchParams.get("action");

    if (!masterId) {
      setHighlightedMasterId(null);
      return;
    }

    const masterIdNum = parseInt(masterId, 10);
    if (!Number.isFinite(masterIdNum)) return;

    setHighlightedMasterId(masterIdNum);

    if (action !== "manage") return;
    if (deepLinkOpenedRef.current === masterIdNum) return;
    if (masterContainers.length === 0) return;

    const master = masterContainers.find((m) => m.id === masterIdNum);
    if (!master) return;

    deepLinkOpenedRef.current = masterIdNum;
    void showContainerModal(master);

    // Keep master_id for highlight; drop action so list refresh won't reopen
    const next = new URLSearchParams(searchParams);
    next.delete("action");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open once when list/URL deep-link is ready
  }, [searchParams, masterContainers, setSearchParams]);

  useEffect(() => {
    if (highlightedMasterId) {
      // Scroll to highlighted row after data loads
      setTimeout(() => {
        const element = document.querySelector(".highlighted-row");
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
    }
  }, [highlightedMasterId, masterContainers]);

  const handleUpdateReference = async () => {
    if (!selectedMaster) return;

    try {
      const values = await containerForm.validateFields([
        "reference",
        "transport_type",
        "agent_id",
        "origin_port",
        "destination_port",
      ]);

      const agentId = values.agent_id ?? null;
      const originPort = values.origin_port ?? null;
      const destinationPort = values.destination_port ?? null;
      const result = await updateMasterContainer(selectedMaster.id, {
        reference: values.reference,
        transport_type: values.transport_type || null,
        agent_id: agentId,
        origin_port: originPort,
        destination_port: destinationPort,
        eta_customer: selectedMaster.eta_customer,
        eta: selectedMaster.eta,
        etd: selectedMaster.etd,
        delayed_etd: selectedMaster.delayed_etd,
        status: selectedMaster.status,
      });

      if (result.error) {
        message.error("Failed to update bill of lading (B/L)");
        return;
      }

      message.success("Bill of lading (B/L) updated successfully");
      loadMasterContainers();
      if (onRefresh) onRefresh();

      setSelectedMaster({
        ...selectedMaster,
        reference: values.reference,
        transport_type: values.transport_type || null,
        agent_id: agentId,
        origin_port: originPort,
        destination_port: destinationPort,
        agent_info: agents.find((a) => a.id === agentId) ?? null,
      });
      await loadAvailableContainers(values.transport_type || null);
    } catch (error) {
      console.error("Error updating reference:", error);
      message.error("Failed to update bill of lading (B/L)");
    }
  };

  /** Admin-only: assign / change agent on a master BL/AWB */
  const handleAgentChange = async (
    masterId: number,
    agentId: number | null
  ) => {
    const result = await updateMasterContainer(masterId, {
      agent_id: agentId,
    });
    if (result.error) {
      message.error("Failed to update agent");
      return;
    }
    message.success(
      agentId != null ? "Agent assigned" : "Agent cleared"
    );
    if (selectedMaster?.id === masterId) {
      setSelectedMaster({
        ...selectedMaster,
        agent_id: agentId,
        agent_info: agents.find((a) => a.id === agentId) ?? null,
      });
      containerForm.setFieldValue("agent_id", agentId ?? undefined);
    }
    loadMasterContainers();
    onRefresh?.();
  };

  const handleTransportTypeChange = async (
    masterId: number,
    transportTypeId: number | null
  ) => {
    const result = await updateMasterContainer(masterId, {
      transport_type: transportTypeId,
    });
    if (result.error) {
      message.error("Failed to update transport type");
      return;
    }
    message.success("Transport type updated");
    const transportInfo =
      transportTypes.find((t) => t.id === transportTypeId) ?? null;
    if (selectedMaster?.id === masterId) {
      setSelectedMaster({
        ...selectedMaster,
        transport_type: transportTypeId,
        transport_type_info: transportInfo
          ? {
              id: transportInfo.id,
              name: transportInfo.name,
              abbreviation: "",
            }
          : null,
      });
      containerForm.setFieldValue(
        "transport_type",
        transportTypeId ?? undefined
      );
      await loadAvailableContainers(transportTypeId);
    }
    loadMasterContainers();
    onRefresh?.();
  };

  // Filter master containers based on search text and status
  const filteredMasterContainers = masterContainers.filter((master) => {
    const searchLower = searchText.toLowerCase();
    const matchesSearch =
      !searchText ||
      master.reference?.toLowerCase().includes(searchLower) ||
      master.id.toString().includes(searchLower);

    const matchesStatus =
      !statusFilter ||
      (statusFilter === "with_containers" &&
        (master.container_count || 0) > 0) ||
      (statusFilter === "empty" && (master.container_count || 0) === 0);

    return matchesSearch && matchesStatus;
  });

  const columns: ColumnsType<MasterContainerWithContainers> = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: "Reference",
      dataIndex: "reference",
      key: "reference",
      render: (text) => text || "-",
      sorter: (a, b) => (a.reference || "").localeCompare(b.reference || ""),
    },
    {
      title: "Transport",
      key: "transport_type",
      width: 160,
      render: (_, record) => (
        <Select
          size="small"
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="Transport type"
          style={{ width: "100%" }}
          value={record.transport_type ?? undefined}
          options={transportTypes.map((t) => ({
            value: t.id,
            label: t.name,
          }))}
          onClick={(e) => e.stopPropagation()}
          onChange={(value) =>
            handleTransportTypeChange(record.id, value ?? null)
          }
        />
      ),
      sorter: (a, b) =>
        getMasterTransportTypeName(a).localeCompare(
          getMasterTransportTypeName(b)
        ),
    },
    {
      title: "Agent",
      key: "agent",
      width: 180,
      render: (_, record) => (
        <Select
          size="small"
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="Assign agent"
          style={{ width: "100%" }}
          value={record.agent_id ?? undefined}
          options={agents.map((agent) => ({
            value: agent.id,
            label: agent.name,
          }))}
          onClick={(e) => e.stopPropagation()}
          onChange={(value) => handleAgentChange(record.id, value ?? null)}
        />
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status) => (status ? <Tag color="blue">{status}</Tag> : "-"),
      sorter: (a, b) => (a.status || "").localeCompare(b.status || ""),
    },
    {
      title: "Origin",
      key: "origin_port",
      width: 140,
      render: (_, record) => {
        const info = Array.isArray(record.origin_port_info)
          ? record.origin_port_info[0]
          : record.origin_port_info;
        return info?.name || "-";
      },
    },
    {
      title: "Destination",
      key: "destination_port",
      width: 140,
      render: (_, record) => {
        const info = Array.isArray(record.destination_port_info)
          ? record.destination_port_info[0]
          : record.destination_port_info;
        return info?.name || "-";
      },
    },
    {
      title: "Customer ETA",
      dataIndex: "eta_customer",
      key: "eta_customer",
      width: 120,
      render: (date) => formatDate(date),
      sorter: (a, b) => {
        const dateA = a.eta_customer ? new Date(a.eta_customer).getTime() : 0;
        const dateB = b.eta_customer ? new Date(b.eta_customer).getTime() : 0;
        return dateA - dateB;
      },
    },
    {
      title: "ETA",
      dataIndex: "eta",
      key: "eta",
      width: 120,
      render: (date) => formatDate(date),
      sorter: (a, b) => {
        const dateA = a.eta ? new Date(a.eta).getTime() : 0;
        const dateB = b.eta ? new Date(b.eta).getTime() : 0;
        return dateA - dateB;
      },
    },
    {
      title: "ETD",
      dataIndex: "etd",
      key: "etd",
      width: 120,
      render: (date) => formatDate(date),
      sorter: (a, b) => {
        const dateA = a.etd ? new Date(a.etd).getTime() : 0;
        const dateB = b.etd ? new Date(b.etd).getTime() : 0;
        return dateA - dateB;
      },
    },
    {
      title: "Delayed ETD",
      dataIndex: "delayed_etd",
      key: "delayed_etd",
      width: 140,
      render: (date) =>
        date ? (
          <span>
            {formatDate(date)}
            <Tag color="orange" style={{ marginLeft: 6 }}>
              Delayed
            </Tag>
          </span>
        ) : (
          "-"
        ),
      sorter: (a, b) => {
        const dateA = a.delayed_etd ? new Date(a.delayed_etd).getTime() : 0;
        const dateB = b.delayed_etd ? new Date(b.delayed_etd).getTime() : 0;
        return dateA - dateB;
      },
    },
    {
      title: "Container Count",
      dataIndex: "container_count",
      key: "container_count",
      width: 120,
      render: (count) => (
        <Tag color={count > 0 ? "blue" : "default"}>
          {count} container{count !== 1 ? "s" : ""}
        </Tag>
      ),
      sorter: (a, b) => (a.container_count || 0) - (b.container_count || 0),
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      width: 120,
      render: (date) => formatDate(date),
      sorter: (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    },
    {
      title: "Actions",
      key: "actions",
      width: 280,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<LinkOutlined />}
            onClick={() => showContainerModal(record)}
            size="small"
          >
            Manage
          </Button>
          <Button
            type="link"
            icon={<SyncOutlined />}
            onClick={() => showBulkUpdateModal(record)}
            size="small"
            disabled={(record.container_count || 0) === 0}
          >
            Bulk Update
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this bill of lading (B/L)?"
            description="This will remove all container associations."
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />} size="small">
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Expandable row: containers, each expandable to bookings
  const expandedRowRender = (record: MasterContainerWithContainers) => (
    <MasterContainersNestedExpand
      containers={record.containers || []}
      masterId={record.id}
      onRemoveContainer={handleRemoveContainer}
      getBookingPath={(bookingId) =>
        `/admin/bookings?booking_id=${bookingId}&tab=products`
      }
      getContainerPath={(containerId) => `/admin/containers/${containerId}`}
    />
  );

  // Add helper function to map container status to booking status
  const mapContainerStatusToBookingStatus = (
    containerStatus: string
  ): string => {
    if (containerStatus === "customs") {
      return "customs_pending_clearance";
    } else if (["planned", "departed", "arrived"].includes(containerStatus)) {
      return "in_transit";
    } else {
      return containerStatus; // For completed or other statuses
    }
  };

  // Enhanced bulk update modal function
  const showBulkUpdateModal = async (master: MasterContainerWithContainers) => {
    setSelectedMasterForBulk(master);
    setPreviewLoading(true);
    setIsBulkUpdateModalVisible(true);

    // Prefill form with current master container data
    bulkUpdateForm.setFieldsValue({
      eta_customer: master.eta_customer
        ? dayjs(master.eta_customer)
        : undefined,
      eta: master.eta ? dayjs(master.eta) : undefined,
      etd: master.etd ? dayjs(master.etd) : undefined,
      delayed_etd: master.delayed_etd ? dayjs(master.delayed_etd) : undefined,
      container_status: master.status || undefined,
      track_and_trace: master.track_and_trace || undefined,
    });

    try {
      const result = await getUpdatePreview(master.id);
      if (result.error) {
        message.error("Failed to load update preview");
      } else {
        setUpdatePreview({
          containers: result.containers,
          bookings: result.bookings,
        });
      }
    } catch (error) {
      console.error("Error loading preview:", error);
      message.error("Failed to load update preview");
    } finally {
      setPreviewLoading(false);
    }
  };

  // Handle container status change to auto-select booking status
  const handleContainerStatusChange = (containerStatus: string) => {
    if (containerStatus) {
      const mappedBookingStatus =
        mapContainerStatusToBookingStatus(containerStatus);
      bulkUpdateForm.setFieldValue("booking_status", mappedBookingStatus);
    } else {
      bulkUpdateForm.setFieldValue("booking_status", undefined);
    }
  };

  // Watch form values for reactive preview
  const watchedContainerStatus = Form.useWatch(
    "container_status",
    bulkUpdateForm
  );
  const watchedBookingStatus = Form.useWatch("booking_status", bulkUpdateForm);
  const watchedEtaCustomer = Form.useWatch("eta_customer", bulkUpdateForm);
  const watchedEta = Form.useWatch("eta", bulkUpdateForm);
  const watchedEtd = Form.useWatch("etd", bulkUpdateForm);
  const watchedDelayedEtd = Form.useWatch("delayed_etd", bulkUpdateForm);
  const watchedTrackAndTrace = Form.useWatch("track_and_trace", bulkUpdateForm);

  // Enhanced bulk update function
  const handleBulkUpdate = async () => {
    if (!selectedMasterForBulk) return;

    try {
      const values = await bulkUpdateForm.validateFields();
      setIsSubmitting(true);

      // Prepare container updates with proper null handling for cleared fields
      const containerUpdates: {
        eta_customer?: string | null;
        eta?: string | null;
        etd?: string | null;
        delayed_etd?: string | null;
        status?: string | null;
        track_and_trace?: string | null;
      } = {};

      // Handle dates - set to null if cleared, format if has value
      if (values.eta_customer !== undefined) {
        containerUpdates.eta_customer = values.eta_customer
          ? dayjs(values.eta_customer).format("YYYY-MM-DD")
          : null;
      }
      if (values.eta !== undefined) {
        containerUpdates.eta = values.eta
          ? dayjs(values.eta).format("YYYY-MM-DD")
          : null;
      }
      if (values.etd !== undefined) {
        containerUpdates.etd = values.etd
          ? dayjs(values.etd).format("YYYY-MM-DD")
          : null;
      }
      if (values.delayed_etd !== undefined) {
        containerUpdates.delayed_etd = values.delayed_etd
          ? dayjs(values.delayed_etd).format("YYYY-MM-DD")
          : null;
      }

      // Handle status - set to null if cleared
      if (values.container_status !== undefined) {
        containerUpdates.status = values.container_status || null;
      }

      // Handle track_and_trace - always include if present in form values
      if (Object.prototype.hasOwnProperty.call(values, "track_and_trace")) {
        // Handle empty string as null (when user clears the field)
        containerUpdates.track_and_trace =
          values.track_and_trace === "" ? null : values.track_and_trace || null;
      }

      // Prepare booking updates
      const bookingUpdates: { status?: string | null } = {};
      if (values.booking_status) {
        bookingUpdates.status = values.booking_status;
      } else if (values.container_status) {
        // Use the predefined mapping if no explicit booking status is provided
        bookingUpdates.status = mapContainerStatusToBookingStatus(
          values.container_status
        );
      }

      // Update master container
      const masterResult = await updateMasterContainer(
        selectedMasterForBulk.id,
        {
          reference: selectedMasterForBulk.reference || undefined,
          eta_customer: containerUpdates.eta_customer,
          eta: containerUpdates.eta,
          etd: containerUpdates.etd,
          delayed_etd: containerUpdates.delayed_etd,
          status: containerUpdates.status,
          track_and_trace: containerUpdates.track_and_trace,
        }
      );

      if (masterResult.error) {
        throw new Error("Failed to update bill of lading (B/L)");
      }

      // Update containers if there are container updates
      if (Object.keys(containerUpdates).length > 0) {
        const containerResult = await bulkUpdateContainersInMaster(
          selectedMasterForBulk.id,
          containerUpdates
        );

        if (!containerResult.success) {
          throw new Error("Failed to update containers");
        }
      }

      // Update bookings if there are booking updates
      if (Object.keys(bookingUpdates).length > 0) {
        const bookingResult = await bulkUpdateBookingsInMaster(
          selectedMasterForBulk.id,
          bookingUpdates
        );

        if (!bookingResult.success) {
          throw new Error("Failed to update bookings");
        }
      }

      message.success("Bulk update completed successfully");
      setIsBulkUpdateModalVisible(false);
      bulkUpdateForm.resetFields();
      setSelectedMasterForBulk(null);
      setUpdatePreview({ containers: [], bookings: [] });
      loadMasterContainers();
      onRefresh?.();
    } catch (error) {
      console.error("Error performing bulk update:", error);
      message.error("Failed to perform bulk update");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col flex="auto">
          <Input
            placeholder="Search bills of lading (B/L) by reference or ID"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            prefix={<SearchOutlined />}
            allowClear
          />
        </Col>
        <Col>
          <Space>
            <Select
              placeholder="Filter by status"
              allowClear
              style={{ width: 150 }}
              onChange={(value) => setStatusFilter(value)}
              value={statusFilter}
            >
              <Option value="with_containers">With Containers</Option>
              <Option value="empty">Empty</Option>
            </Select>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => showModal()}
            >
              Add bill of lading (B/L)
            </Button>
            <Button icon={<ReloadOutlined />} onClick={loadMasterContainers}>
              Refresh
            </Button>
          </Space>
        </Col>
      </Row>

      <CompactTable>
        <Table
          columns={columns}
          dataSource={filteredMasterContainers}
          loading={loading}
          rowKey="id"
          expandable={{
            expandedRowRender,
            rowExpandable: (record) => (record.containers?.length || 0) > 0,
          }}
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} bills of lading`,
          }}
          className="compact-table"
          size="small"
          rowClassName={(record) =>
            highlightedMasterId === record.id ? "highlighted-row" : ""
          }
          style={
            {
              "--highlighted-row-bg": "#e6f7ff",
            } as React.CSSProperties & { [key: string]: string }
          }
          components={{
            body: {
              row: ({
                className,
                ...props
              }: {
                className?: string;
                style?: React.CSSProperties;
              }) => (
                <tr
                  {...props}
                  className={className}
                  style={{
                    ...(props.style || {}),
                    backgroundColor: className?.includes("highlighted-row")
                      ? "#e6f7ff"
                      : undefined,
                  }}
                />
              ),
            },
          }}
        />
      </CompactTable>

      {/* Add bill of lading (B/L) modal */}
      <Modal
        title="Add bill of lading (B/L)"
        open={isModalVisible}
        onOk={handleSubmit}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        confirmLoading={loading}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="reference"
                label="Reference"
                rules={[
                  { required: true, message: "Please enter a reference" },
                  {
                    max: 100,
                    message: "Reference cannot exceed 100 characters",
                  },
                ]}
              >
                <Input placeholder="Enter B/L reference" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="transport_type"
                label="Transport type"
                rules={[
                  { required: true, message: "Please select a transport type" },
                ]}
              >
                <Select placeholder="Select transport type">
                  {transportTypes.map((t) => (
                    <Option key={t.id} value={t.id}>
                      {t.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="agent_id"
                label="Agent"
                extra="Only admins can assign or change the agent."
              >
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="Select agent"
                  options={agents.map((agent) => ({
                    value: agent.id,
                    label: agent.name,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="status" label="Status">
                <Select placeholder="Select status" allowClear>
                  <Option value="planned">Planned</Option>
                  <Option value="departed">Departed</Option>
                  <Option value="arrived">Arrived</Option>
                  <Option value="customs">Customs</Option>
                  <Option value="completed">Completed</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <MasterContainerPortFields
            originPorts={originPorts}
            destinationPorts={destinationPorts}
            required
          />
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item
                name="etd"
                label="ETD (original)"
                tooltip="Original booked departure — used for invoicing"
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="delayed_etd"
                label="Delayed ETD"
                tooltip="Optional operational delayed departure"
              >
                <DatePicker style={{ width: "100%" }} allowClear />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="eta" label="ETA">
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="eta_customer" label="Customer ETA">
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="track_and_trace" label="Track & Trace">
                <Input placeholder="Enter track and trace information" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Container Management Modal */}
      <Modal
        title={`Manage containers — ${
          selectedMaster?.reference || "Bill of lading (B/L)"
        }`}
        open={isContainerModalVisible}
        onCancel={() => {
          setIsContainerModalVisible(false);
          setSelectedMaster(null);
          containerForm.resetFields();
        }}
        footer={null}
        width={800}
      >
        <Card title="Bill of lading (B/L) details" style={{ marginBottom: 16 }}>
          <Form form={containerForm} layout="vertical">
            <Row gutter={12}>
              <Col span={8}>
                <Form.Item
                  name="reference"
                  label="Reference"
                  rules={[
                    { required: true, message: "Please enter a reference" },
                    {
                      max: 100,
                      message: "Reference cannot exceed 100 characters",
                    },
                  ]}
                >
                  <Input placeholder="Enter B/L reference" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="transport_type"
                  label="Transport type"
                  rules={[
                    {
                      required: true,
                      message: "Please select a transport type",
                    },
                  ]}
                >
                  <Select placeholder="Select transport type">
                    {transportTypes.map((t) => (
                      <Option key={t.id} value={t.id}>
                        {t.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="agent_id"
                  label="Agent"
                  extra="Only admins can assign or change the agent."
                >
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="Select agent"
                    options={agents.map((agent) => ({
                      value: agent.id,
                      label: agent.name,
                    }))}
                  />
                </Form.Item>
              </Col>
            </Row>
            <MasterContainerPortFields
              originPorts={originPorts}
              destinationPorts={destinationPorts}
              required
            />
            <Button
              type="primary"
              onClick={handleUpdateReference}
              loading={loading}
            >
              Update details
            </Button>
          </Form>
        </Card>

        <Card title="Add Container" style={{ marginBottom: 16 }}>
          <Form
            form={containerForm}
            layout="inline"
            onFinish={handleAddContainer}
          >
            <Form.Item
              name="container_id"
              rules={[{ required: true, message: "Please select a container" }]}
              style={{ flex: 1 }}
            >
              <Select
                placeholder="Select a container to add"
                showSearch
                optionFilterProp="children"
                style={{ width: "100%" }}
              >
                {availableContainers.map((container) => (
                  <Option key={container.id} value={container.id}>
                    {container.reference || `Container ${container.id}`} -{" "}
                    {container.status}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                icon={<LinkOutlined />}
              >
                Add Container
              </Button>
            </Form.Item>
          </Form>
        </Card>

        {selectedMaster && (
          <MasterContainerAwbBlSection
            masterContainerId={selectedMaster.id}
            masterReference={selectedMaster.reference}
            style={{ marginBottom: 16 }}
          />
        )}

        {selectedMaster &&
          selectedMaster.containers &&
          selectedMaster.containers.length > 0 && (
            <Card title="Current Containers">
              <Table
                columns={[
                  {
                    title: "Container ID",
                    dataIndex: "id",
                    key: "id",
                  },
                  {
                    title: "Reference",
                    dataIndex: "reference",
                    key: "reference",
                    render: (text) => text || "-",
                  },
                  {
                    title: "Status",
                    dataIndex: "status",
                    key: "status",
                    render: (status) => (
                      <Tag color={status === "completed" ? "green" : "blue"}>
                        {status || "Unknown"}
                      </Tag>
                    ),
                  },
                  {
                    title: "Actions",
                    key: "actions",
                    render: (_, container) => (
                      <Popconfirm
                        title="Remove this container?"
                        onConfirm={() =>
                          handleRemoveContainer(selectedMaster.id, container.id)
                        }
                        okText="Yes"
                        cancelText="No"
                      >
                        <Button
                          type="link"
                          danger
                          icon={<DisconnectOutlined />}
                          size="small"
                        >
                          Remove
                        </Button>
                      </Popconfirm>
                    ),
                  },
                ]}
                dataSource={selectedMaster.containers}
                pagination={false}
                size="small"
                rowKey="id"
              />
            </Card>
          )}
      </Modal>

      {/* Enhanced Bulk Update Modal */}
      <Modal
        title={`Bulk update: ${
          selectedMasterForBulk?.reference ||
          `B/L #${selectedMasterForBulk?.id}`
        }`}
        open={isBulkUpdateModalVisible}
        onCancel={() => {
          setIsBulkUpdateModalVisible(false);
          bulkUpdateForm.resetFields();
          setSelectedMasterForBulk(null);
          setUpdatePreview({ containers: [], bookings: [] });
        }}
        onOk={handleBulkUpdate}
        confirmLoading={isSubmitting}
        width={1000}
        style={{ top: 20 }}
      >
        <Alert
          message="Bulk Update Warning"
          description="This will update all containers and bookings under this bill of lading (B/L). Please review the changes carefully before proceeding."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form form={bulkUpdateForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Title level={5}>Container Updates</Title>
              <Form.Item name="eta_customer" label="Customer ETA">
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item name="eta" label="ETA">
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item
                name="etd"
                label="ETD (original)"
                tooltip="Original booked departure — used for invoicing"
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item
                name="delayed_etd"
                label="Delayed ETD"
                tooltip="Optional operational delayed departure. When set, shown instead of original ETD."
              >
                <DatePicker style={{ width: "100%" }} allowClear />
              </Form.Item>
              <Form.Item name="container_status" label="Container Status">
                <Select
                  placeholder="Select container status"
                  allowClear
                  onChange={handleContainerStatusChange}
                >
                  <Option value="planned">Planned</Option>
                  <Option value="departed">Departed</Option>
                  <Option value="arrived">Arrived</Option>
                  <Option value="customs">Customs</Option>
                  <Option value="completed">Completed</Option>
                </Select>
              </Form.Item>
              <Form.Item name="track_and_trace" label="Track & Trace">
                <Input placeholder="Enter track and trace information" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Title level={5}>Booking Updates</Title>
              <Form.Item
                name="booking_status"
                label="Booking Status"
                tooltip="If not specified, booking status will be automatically mapped from container status"
              >
                <Select
                  placeholder="Select booking status (optional)"
                  allowClear
                >
                  <Option value="concept">Concept</Option>
                  <Option value="submitted">Pending Approval</Option>
                  <Option value="approved">Approved</Option>
                  <Option value="waiting_for_goods">Waiting for Goods</Option>
                  <Option value="goods_received_origin">
                    Goods Received at Origin
                  </Option>
                  <Option value="in_transit">In Transit</Option>
                  <Option value="customs_pending_clearance">
                    Pending Customs Clearance
                  </Option>
                  <Option value="customs_cleared">Customs Cleared</Option>
                  <Option value="fyco">Fyco</Option>
                  <Option value="out_for_delivery">Out for Delivery</Option>
                  <Option value="delivered">Delivered</Option>
                  <Option value="completed">Completed</Option>
                  <Option value="cancelled">Cancelled</Option>
                </Select>
              </Form.Item>
              <Alert
                message="Automatic Status Mapping"
                description={
                  <div>
                    <p>
                      When you select a container status, the booking status is
                      automatically mapped:
                    </p>
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      <li>
                        <strong>customs</strong> → customs_pending_clearance
                      </li>
                      <li>
                        <strong>planned/departed/arrived</strong> → in_transit
                      </li>
                      <li>
                        <strong>completed</strong> → completed
                      </li>
                    </ul>
                    <p style={{ margin: "8px 0 0 0", fontStyle: "italic" }}>
                      You can override the automatic selection by choosing a
                      different booking status.
                    </p>
                  </div>
                }
                type="info"
                showIcon
                style={{ marginTop: 8 }}
              />
            </Col>
          </Row>

          <Divider />

          {/* Enhanced Preview Section */}
          <div style={{ marginTop: 16 }}>
            <Title level={5}>Preview of Changes</Title>
            {previewLoading ? (
              <div style={{ textAlign: "center", padding: "20px" }}>
                <Spin size="large" />
                <div style={{ marginTop: 8 }}>Loading preview...</div>
              </div>
            ) : (
              <>
                <Row gutter={16}>
                  <Col span={24}>
                    <Card
                      title={`Containers (${updatePreview.containers.length})`}
                      size="small"
                    >
                      {updatePreview.containers.length === 0 ? (
                        <Text type="secondary">No containers to update</Text>
                      ) : (
                        <Table
                          dataSource={updatePreview.containers}
                          rowKey="id"
                          pagination={false}
                          size="small"
                          columns={[
                            {
                              title: "Container",
                              dataIndex: "reference",
                              key: "reference",
                              width: 120,
                              render: (ref, record) => (
                                <Text strong>
                                  {ref || `Container ${record.id}`}
                                </Text>
                              ),
                            },
                            {
                              title: "Current Status",
                              dataIndex: "status",
                              key: "currentStatus",
                              width: 100,
                              render: (status) => (
                                <Tag color={status ? "blue" : "default"}>
                                  {status || "No Status"}
                                </Tag>
                              ),
                            },
                            {
                              title: "New Status",
                              key: "newStatus",
                              width: 100,
                              render: () => {
                                return watchedContainerStatus ? (
                                  <Tag color="green">
                                    {watchedContainerStatus}
                                  </Tag>
                                ) : watchedContainerStatus === undefined ? (
                                  <Text type="secondary">No change</Text>
                                ) : (
                                  <Tag color="orange">N/A</Tag>
                                );
                              },
                            },
                            {
                              title: "Current Customer ETA",
                              dataIndex: "eta_customer",
                              key: "currentEtaCustomer",
                              width: 130,
                              render: (date) => formatDate(date) || "-",
                            },
                            {
                              title: "New Customer ETA",
                              key: "newEtaCustomer",
                              width: 130,
                              render: () => {
                                return watchedEtaCustomer ? (
                                  <Tag color="green">
                                    {dayjs(watchedEtaCustomer).format(
                                      "MMM DD, YYYY"
                                    )}
                                  </Tag>
                                ) : watchedEtaCustomer === undefined ? (
                                  <Text type="secondary">No change</Text>
                                ) : (
                                  <Tag color="orange">N/A</Tag>
                                );
                              },
                            },
                            {
                              title: "Current ETA",
                              dataIndex: "eta",
                              key: "currentEta",
                              width: 110,
                              render: (date) => formatDate(date) || "-",
                            },
                            {
                              title: "New ETA",
                              key: "newEta",
                              width: 110,
                              render: () => {
                                return watchedEta ? (
                                  <Tag color="green">
                                    {dayjs(watchedEta).format("MMM DD, YYYY")}
                                  </Tag>
                                ) : watchedEta === undefined ? (
                                  <Text type="secondary">No change</Text>
                                ) : (
                                  <Tag color="orange">N/A</Tag>
                                );
                              },
                            },
                            {
                              title: "Current ETD",
                              dataIndex: "etd",
                              key: "currentEtd",
                              width: 110,
                              render: (_date, record) =>
                                formatDate(record.delayed_etd || record.etd) ||
                                "-",
                            },
                            {
                              title: "New ETD",
                              key: "newEtd",
                              width: 110,
                              render: () => {
                                return watchedEtd ? (
                                  <Tag color="green">
                                    {dayjs(watchedEtd).format("MMM DD, YYYY")}
                                  </Tag>
                                ) : watchedEtd === undefined ? (
                                  <Text type="secondary">No change</Text>
                                ) : (
                                  <Tag color="orange">N/A</Tag>
                                );
                              },
                            },
                            {
                              title: "New Delayed ETD",
                              key: "newDelayedEtd",
                              width: 120,
                              render: () => {
                                return watchedDelayedEtd ? (
                                  <Tag color="orange">
                                    {dayjs(watchedDelayedEtd).format(
                                      "MMM DD, YYYY"
                                    )}
                                  </Tag>
                                ) : watchedDelayedEtd === undefined ? (
                                  <Text type="secondary">No change</Text>
                                ) : (
                                  <Tag color="orange">Cleared</Tag>
                                );
                              },
                            },
                            {
                              title: "Current Track & Trace",
                              dataIndex: "track_and_trace",
                              key: "currentTrackAndTrace",
                              width: 150,
                              render: (trackAndTrace) => trackAndTrace || "-",
                            },
                            {
                              title: "New Track & Trace",
                              key: "newTrackAndTrace",
                              width: 150,
                              render: () => {
                                return watchedTrackAndTrace ? (
                                  <Tag color="green">
                                    {watchedTrackAndTrace}
                                  </Tag>
                                ) : watchedTrackAndTrace === undefined ? (
                                  <Text type="secondary">No change</Text>
                                ) : (
                                  <Tag color="orange">N/A</Tag>
                                );
                              },
                            },
                          ]}
                          scroll={{ x: 1200 }}
                        />
                      )}
                    </Card>
                  </Col>
                </Row>

                <Row gutter={16} style={{ marginTop: 16 }}>
                  <Col span={24}>
                    <Card
                      title={`Bookings (${updatePreview.bookings.length})`}
                      size="small"
                    >
                      {updatePreview.bookings.length === 0 ? (
                        <Text type="secondary">No bookings to update</Text>
                      ) : (
                        <Table
                          dataSource={updatePreview.bookings}
                          rowKey="id"
                          pagination={false}
                          size="small"
                          columns={[
                            {
                              title: "Booking",
                              dataIndex: "id",
                              key: "id",
                              render: (id) => {
                                // For now, default to SEA - we can enhance this later with transport type data
                                return <Tag color="blue">EFF{id}SEA</Tag>;
                              },
                            },
                            {
                              title: "Current Status",
                              dataIndex: "status",
                              key: "currentStatus",
                              render: (status) => {
                                const normalizedStatus =
                                  normalizeBookingStatus(status);
                                const statusColor =
                                  statusColors[
                                    normalizedStatus as keyof typeof statusColors
                                  ] || "default";
                                return (
                                  <Tag color={statusColor}>
                                    {normalizedStatus}
                                  </Tag>
                                );
                              },
                            },
                            {
                              title: "New Status",
                              key: "newStatus",
                              render: () => {
                                let newStatus = "";
                                if (watchedBookingStatus) {
                                  newStatus = watchedBookingStatus;
                                } else if (watchedContainerStatus) {
                                  newStatus = mapContainerStatusToBookingStatus(
                                    watchedContainerStatus
                                  );
                                }

                                if (newStatus) {
                                  const normalizedStatus =
                                    normalizeBookingStatus(newStatus);
                                  const statusColor =
                                    statusColors[
                                      normalizedStatus as keyof typeof statusColors
                                    ] || "green";
                                  return (
                                    <Tag color={statusColor}>
                                      {normalizedStatus}
                                    </Tag>
                                  );
                                } else {
                                  return (
                                    <Text type="secondary">No change</Text>
                                  );
                                }
                              },
                            },
                          ]}
                        />
                      )}
                    </Card>
                  </Col>
                </Row>
              </>
            )}
          </div>

          {/* Summary */}
          <div
            style={{
              marginTop: 16,
              padding: 12,
              backgroundColor: "#f5f5f5",
              borderRadius: 4,
            }}
          >
            <Row gutter={16}>
              <Col span={8}>
                <Text strong>Containers to update: </Text>
                <Text>{updatePreview.containers.length}</Text>
              </Col>
              <Col span={8}>
                <Text strong>Bookings to update: </Text>
                <Text>{updatePreview.bookings.length}</Text>
              </Col>
              <Col span={8}>
                <Text strong>Total affected items: </Text>
                <Text>
                  {updatePreview.containers.length +
                    updatePreview.bookings.length}
                </Text>
              </Col>
            </Row>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default MasterContainersList;
